import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  pollPairing,
  startPairing,
  type DisplaySeat,
} from "../../services/displayService.js";

/**
 * How often to ask whether anybody has typed the code.
 *
 * Two seconds is the gap between somebody pressing "Connect" on their phone and
 * the television reacting, which is the whole of what this screen is judged on.
 */
const POLL_MS = 2000;

/**
 * "Connect this TV" — the screen a packaged television shows before it has a
 * party (phase 11c).
 *
 * # The pairing is inverted, and this is the half that made it necessary
 *
 * In a browser the television shows a QR and the phone scans it. In an app there
 * is no URL to encode and no keyboard worth typing a room code on (ADR 0005
 * refused one on purpose), so it runs the other way: **the television shows a
 * code and the host types it into the phone**, which already has a keyboard and
 * is already in the lobby.
 *
 * # Everything on this screen is read at three metres
 *
 * The code is the largest thing on it, grouped in threes because six characters
 * in a row is a transcription error waiting to happen. There is nothing to
 * focus, nothing to click and no hover state: nobody is holding a pointer, and
 * the only input this screen has is somebody's phone.
 *
 * # It re-pairs on its own
 *
 * A code expires after ten minutes, which is exactly the case a television meets
 * — switched on, then left while everybody talks. `gone` is answered by asking
 * for a new code rather than by a message nobody is standing there to read.
 */
export function PairPanel({
  onPaired,
  pollMs = POLL_MS,
}: {
  onPaired: (seat: DisplaySeat) => void;
  /** Overridable so a test does not spend two seconds per assertion. */
  pollMs?: number;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Held in a ref rather than in state: it is a credential the screen must never
  // render, and a state value is one careless `{poll}` away from being on a
  // television.
  const poll = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const begin = async () => {
      try {
        const pairing = await startPairing();
        if (cancelled) {
          return;
        }
        poll.current = pairing.poll_token;
        setCode(pairing.code);
        setError(null);
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : t("tv.pair.unreachable"));
        }
      }
    };

    const ask = async () => {
      const token = poll.current;
      if (!token) {
        await begin();
        return;
      }
      try {
        const progress = await pollPairing(token);
        if (cancelled) {
          return;
        }
        if (
          progress.status === "paired" &&
          progress.device_token &&
          progress.party_code
        ) {
          poll.current = null;
          onPaired({ token: progress.device_token, code: progress.party_code });
          return;
        }
        if (progress.status === "gone") {
          // Expired, or collected. Either way this television needs a fresh
          // code, and asking for one is something it can do by itself.
          poll.current = null;
          setCode(null);
          await begin();
        }
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : t("tv.pair.unreachable"));
        }
      }
    };

    void begin();
    const timer = window.setInterval(() => {
      void ask();
    }, pollMs);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // `t` is a dependency because the effect renders one of its messages. It
    // only changes when somebody switches language, and the cost of that is a
    // fresh pairing code — which this screen already says is normal.
  }, [onPaired, pollMs, t]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-10 p-12 text-center">
      <h1 className="text-5xl font-semibold">{t("tv.pair.title")}</h1>
      <p className="max-w-3xl text-2xl text-white/60">
        <Trans i18nKey="tv.pair.lead" components={{ action: <strong /> }} />
      </p>

      {code ? (
        <p
          data-testid="pairing-code"
          // Grouped in threes and spaced wide: six characters read across a room
          // are transcribed one group at a time.
          className="font-mono text-[7rem] font-bold leading-none tracking-[0.15em] text-flash-400"
        >
          {code.slice(0, 3)} {code.slice(3)}
        </p>
      ) : (
        <p className="text-3xl text-white/30">…</p>
      )}

      {error && (
        // Not a retry button: nobody is standing at a television to press one,
        // and the poll below keeps trying on its own.
        <p role="alert" className="max-w-3xl text-xl text-wrong-500">
          {error}
        </p>
      )}

      <p className="text-xl text-white/40">{t("tv.pair.rotates")}</p>
    </div>
  );
}
